import React, { useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import StyledBox from 'views/common/components/UI/DamageWaiverTable/DamageWaiverTable.styles';
import { useTranslation } from 'next-i18next';
import useAppNavigator from 'views/rebranded-module/common/hooks/useAppNavigator';
import PATHS from 'lib/navigation/paths';

const DamageWaiverTable = (props) => {
  const {
    className,
    vipZeroPlusTheftDamageWaiver,
    vipSelectPlusTheftDamageWaiver,
    vipBasicPlusTheftDamageWaiver,
    noDamageWaiverSelected,
    // Old props - only show if true
    vipPlusTheftDamageWaiver, // Old VIP column
    vipZeroOriginalTheftDamageWaiver, // Old VIP-ZERO column (was missing!)
  } = props;
  const appNavigator = useAppNavigator();
  const { t } = useTranslation('fe_er_common_damagewaiver_table');

  // Old columns only show if they are true (accepted)
  const shouldShowOldVip = vipPlusTheftDamageWaiver;
  const shouldShowOldVipZero = vipZeroOriginalTheftDamageWaiver; // Fixed!

  const createData = (
    desc,
    noCoverage,
    vipZero,
    vipSelect,
    vipBasic,
    oldVip,
    oldVipZero,
  ) => ({
    desc,
    noCoverage,
    vipZero,
    vipSelect,
    vipBasic,
    oldVip,
    oldVipZero,
  });

  const damageTableData = useMemo(() => {
    const rows = [
      createData(
        `${t(
          'fe_er_common_damagewaiver_table:damage_waiver',
        )} <a class="DWTLink" target='_blank' href=${appNavigator.getUrl(
          undefined,
          PATHS.rentals.rentalTerms().url,
          true,
        )}>${t('fe_er_common_damagewaiver_table:temrs_and_conditions')}</a>`,
        t('fe_er_common_damagewaiver_table:full_value_damages'),
        t('fe_er_common_damagewaiver_table:new_deductible_0'), // New format
        t('fe_er_common_damagewaiver_table:new_deductible_1000'), // New format
        t('fe_er_common_damagewaiver_table:new_deductible_2000'), // New format
        t('fe_er_common_damagewaiver_table:deductible_2000'), // Old VIP (old format)
        t('fe_er_common_damagewaiver_table:deductible_0'), // Old VIP Zero (old format)
      ),
      createData(
        t('fe_er_common_damagewaiver_table:theft_coverage'),
        t('fe_er_common_damagewaiver_table:full_value_motorcycle'),
        t('fe_er_common_damagewaiver_table:theft_new_deductible_1000'), // Updated to use new theft-specific key
        t('fe_er_common_damagewaiver_table:theft_new_deductible_2500'), // Updated to use new theft-specific key
        t('fe_er_common_damagewaiver_table:theft_new_deductible_5000'), // Updated to use new theft-specific key
        t('fe_er_common_damagewaiver_table:deductible_5000'), // Old VIP (old format)
        t('fe_er_common_damagewaiver_table:deductible_1000'), // Old VIP Zero (old format)
      ),
      createData(
        t(
          'fe_er_common_damagewaiver_table:vacation_interruption_protection_vip',
        ),
        t('fe_er_common_damagewaiver_table:refund_of_daily_rate_or_portion'),
        t(
          'fe_er_common_damagewaiver_table:refund_of_daily_rate_and_100_dollar',
        ),
        t('fe_er_common_damagewaiver_table:refund_of_daily_rate_and_50_dollar'),
        t('fe_er_common_damagewaiver_table:refund_of_daily_rate_and_35_dollar'),
        t('fe_er_common_damagewaiver_table:refund_of_daily_rate_and_35_dollar'), // Old VIP
        t(
          'fe_er_common_damagewaiver_table:refund_of_daily_rate_and_100_dollar',
        ), // Old VIP Zero
      ),
      createData(
        t('fe_er_common_damagewaiver_table:personal_property_loss_protection'),
        t('fe_er_common_damagewaiver_table:not_included'),
        t('fe_er_common_damagewaiver_table:renter_coverage'),
        t('fe_er_common_damagewaiver_table:renter_coverage_250'),
        t('fe_er_common_damagewaiver_table:not_included'),
        t('fe_er_common_damagewaiver_table:not_included'), // Old VIP
        t('fe_er_common_damagewaiver_table:renter_coverage'), // Old VIP Zero
      ),
      createData(
        t('fe_er_common_damagewaiver_table:additional_driver'),
        t('fe_er_common_damagewaiver_table:not_included'),
        t('fe_er_common_damagewaiver_table:additional_renter_permitted'),
        t('fe_er_common_damagewaiver_table:not_included'),
        t('fe_er_common_damagewaiver_table:not_included'),
        t('fe_er_common_damagewaiver_table:not_included'), // Old VIP
        t('fe_er_common_damagewaiver_table:additional_renter_permitted'), // Old VIP Zero
      ),
      createData(
        t('fe_er_common_damagewaiver_table:flat_tire_coverage'),
        t('fe_er_common_damagewaiver_table:not_included'),
        t('fe_er_common_damagewaiver_table:covers_tire_repair_100'),
        t('fe_er_common_damagewaiver_table:not_included'),
        t('fe_er_common_damagewaiver_table:not_included'),
        t('fe_er_common_damagewaiver_table:not_included'), // Old VIP
        t('fe_er_common_damagewaiver_table:covers_tire_repair_100'), // Old VIP Zero
      ),
      createData(
        t('fe_er_common_damagewaiver_table:security_deposit_strong'),
        t('fe_er_common_damagewaiver_table:deposit_5000'),
        t('fe_er_common_damagewaiver_table:deposit_100'),
        t('fe_er_common_damagewaiver_table:deposit_1000'),
        t('fe_er_common_damagewaiver_table:deposit_2000'),
        t('fe_er_common_damagewaiver_table:deposit_2000'), // Old VIP
        t('fe_er_common_damagewaiver_table:deposit_100'), // Old VIP Zero
      ),
    ];

    // Only add accept/decline rows if at least one option is selected
    if (
      vipZeroPlusTheftDamageWaiver
      || vipSelectPlusTheftDamageWaiver
      || vipBasicPlusTheftDamageWaiver
      || noDamageWaiverSelected
      || shouldShowOldVip
      || shouldShowOldVipZero
    ) {
      rows.push(
        createData(
          t('fe_er_common_damagewaiver_table:accept_theft_damage_waiver'),
          noDamageWaiverSelected
            ? t('fe_er_common_damagewaiver_table:accepted')
            : '',
          vipZeroPlusTheftDamageWaiver
            ? t('fe_er_common_damagewaiver_table:accepted')
            : '',
          vipSelectPlusTheftDamageWaiver
            ? t('fe_er_common_damagewaiver_table:accepted')
            : '',
          vipBasicPlusTheftDamageWaiver
            ? t('fe_er_common_damagewaiver_table:accepted')
            : '',
          shouldShowOldVip ? t('fe_er_common_damagewaiver_table:accepted') : '',
          shouldShowOldVipZero
            ? t('fe_er_common_damagewaiver_table:accepted')
            : '',
        ),
      );

      rows.push(
        createData(
          t('fe_er_common_damagewaiver_table:decline_theft_damage_waiver'),
          noDamageWaiverSelected
            ? ''
            : t('fe_er_common_damagewaiver_table:declined'),
          vipZeroPlusTheftDamageWaiver
            ? ''
            : t('fe_er_common_damagewaiver_table:declined'),
          vipSelectPlusTheftDamageWaiver
            ? ''
            : t('fe_er_common_damagewaiver_table:declined'),
          vipBasicPlusTheftDamageWaiver
            ? ''
            : t('fe_er_common_damagewaiver_table:declined'),
          shouldShowOldVip ? '' : t('fe_er_common_damagewaiver_table:declined'),
          shouldShowOldVipZero
            ? ''
            : t('fe_er_common_damagewaiver_table:declined'),
        ),
      );
    }

    return rows;
  }, [
    vipZeroPlusTheftDamageWaiver,
    vipSelectPlusTheftDamageWaiver,
    vipBasicPlusTheftDamageWaiver,
    noDamageWaiverSelected,
    shouldShowOldVip,
    shouldShowOldVipZero,
  ]);

  return (
    <StyledBox className={className}>
      <TableContainer>
        <Table className="RPTable">
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography
                  variant="body2"
                  component="p"
                  dangerouslySetInnerHTML={{
                    __html: t('fe_er_common_damagewaiver_table:main_header'),
                  }}
                />
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  component="p"
                  dangerouslySetInnerHTML={{
                    __html: t(
                      'fe_er_common_damagewaiver_table:no_coverage_purchase',
                    ),
                  }}
                />
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  component="p"
                  dangerouslySetInnerHTML={{
                    __html: t(
                      'fe_er_common_damagewaiver_table:vip_zero_vacation',
                    ),
                  }}
                />
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  component="p"
                  dangerouslySetInnerHTML={{
                    __html: t(
                      'fe_er_common_damagewaiver_table:vip_select_vacation',
                    ),
                  }}
                />
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  component="p"
                  dangerouslySetInnerHTML={{
                    __html: t(
                      'fe_er_common_damagewaiver_table:vip_basic_vacation',
                    ),
                  }}
                />
              </TableCell>
              {shouldShowOldVip && (
                <TableCell>
                  <Typography
                    variant="body2"
                    component="p"
                    dangerouslySetInnerHTML={{
                      __html: t('fe_er_common_damagewaiver_table:vip_vacation'),
                    }}
                  />
                </TableCell>
              )}
              {shouldShowOldVipZero && (
                <TableCell>
                  <Typography
                    variant="body2"
                    component="p"
                    dangerouslySetInnerHTML={{
                      __html: t(
                        'fe_er_common_damagewaiver_table:vip_0_vacation',
                      ),
                    }}
                  />
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {damageTableData.map((row) => (
              <TableRow key={row.desc}>
                <TableCell className="RPTRowTh" component="th" scope="row">
                  <Typography
                    variant="body2"
                    component="p"
                    dangerouslySetInnerHTML={{ __html: row.desc }}
                  />
                </TableCell>

                <TableCell>
                  <Typography
                    variant="body2"
                    component="p"
                    dangerouslySetInnerHTML={{ __html: row.noCoverage }}
                  />
                </TableCell>

                <TableCell>
                  <Typography
                    variant="body2"
                    component="p"
                    dangerouslySetInnerHTML={{ __html: row.vipZero }}
                  />
                </TableCell>

                <TableCell>
                  <Typography
                    variant="body2"
                    component="p"
                    dangerouslySetInnerHTML={{ __html: row.vipSelect }}
                  />
                </TableCell>

                <TableCell>
                  <Typography
                    variant="body2"
                    component="p"
                    dangerouslySetInnerHTML={{ __html: row.vipBasic }}
                  />
                </TableCell>

                {shouldShowOldVip && (
                  <TableCell>
                    <Typography
                      variant="body2"
                      component="p"
                      dangerouslySetInnerHTML={{ __html: row.oldVip }}
                    />
                  </TableCell>
                )}

                {shouldShowOldVipZero && (
                  <TableCell>
                    <Typography
                      variant="body2"
                      component="p"
                      dangerouslySetInnerHTML={{ __html: row.oldVipZero }}
                    />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </StyledBox>
  );
};

export { DamageWaiverTable };
